import posthog from 'posthog-js'
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals'
import { attribution, browserAllowed, browserProperties, cleanProperties, events, secondaryMktHost, safePath, type Surface } from '../../../../shared/analytics/policy'
export interface AnalyticsConfig { token?: string; host?: string; environment?: string; testRunId?: string; surface: Surface }
let config: AnalyticsConfig | undefined
let internalUser = false
let started = false, activePath = '', userId: string | undefined
let elapsed = 0, lastTick = 0, milestones = new Set<number>()
let entry: Record<string, string> = {}, first: Record<string, string> = {}, session: Record<string, string> = {}
let lastSession = ''
let sectionObserver: IntersectionObserver | undefined
let sections = new Set<string>()
const consentKey = 'smkt_analytics_consent_v1'
function cookieDomain() { return secondaryMktHost(location.hostname) ? '; Domain=.secondarymkt.com' : '' }
function readCookie(key: string) { try { return decodeURIComponent(document.cookie.split('; ').find(x => x.startsWith(key + '='))?.slice(key.length + 1) ?? '') } catch { return '' } }
function writeCookie(key: string, value: string, age = 31536000) { document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${age}; SameSite=Lax${cookieDomain()}${location.protocol === 'https:' ? '; Secure' : ''}` }
export function consent(): 'yes' | 'no' | '' { const v = readCookie(consentKey); return v === 'yes' || v === 'no' ? v : '' }
function permitted() { return consent() === 'yes' && navigator.doNotTrack !== '1' && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl }
export function configured() { return Boolean(config?.token && ['https://us.i.posthog.com', 'https://eu.i.posthog.com'].includes(config.host ?? '') && browserAllowed(location.hostname, config.environment ?? '')) }
export function setConsent(allow: boolean) {
  writeCookie(consentKey, allow ? 'yes' : 'no')
  if (allow) start()
  else { if (started) { posthog.opt_out_capturing(); posthog.reset(true) }; userId = undefined; internalUser = false; activePath = ''; lastSession = ''; elapsed = 0; writeCookie('smkt_analytics_first_v1', '', 0); writeCookie('smkt_analytics_session_v1', '', 0); first = {}; session = {}; sectionObserver?.disconnect() }
  window.dispatchEvent(new Event('smkt:analytics-consent'))
}
function context() {
  const sid = posthog.get_session_id()
  if (sid !== lastSession) {
    lastSession = sid
    let saved: { id?: string; properties?: Record<string, unknown> } = {}
    try { saved = JSON.parse(readCookie('smkt_analytics_session_v1') || '{}') } catch { /* Missing attribution. */ }
    session = saved.id === sid ? cleanProperties(saved.properties ?? {}) as Record<string, string> : entry
    writeCookie('smkt_analytics_session_v1', JSON.stringify({ id: sid, properties: session }), 86400)
  }
  return { site_surface: config!.surface, analytics_environment: config!.environment, is_test_event: config!.environment !== 'production', ...(config!.environment === 'test' ? { test_run_id: config!.testRunId } : {}), schema_version: 1, is_internal: internalUser, page_path: safePath(location.pathname), ...Object.fromEntries(Object.entries(first).map(([k,v]) => [`first_${k}`,v])), ...Object.fromEntries(Object.entries(session).map(([k,v]) => [`session_${k}`,v])), ...session }
}
function start() {
  if (!configured() || !permitted()) return
  if (started) { posthog.opt_in_capturing({ captureEventName: false }); pageview(); return }
  posthog.init(config!.token!, {
    api_host: config!.host!, persistence: 'cookie', cross_subdomain_cookie: secondaryMktHost(location.hostname), secure_cookie: location.protocol === 'https:',
    capture_pageview: false, capture_pageleave: false, autocapture: false, capture_heatmaps: false,
    disable_session_recording: true, capture_exceptions: false, capture_performance: false,
    enable_recording_console_log: false, advanced_disable_flags: true, disable_external_dependency_loading: true,
    save_referrer: false, save_campaign_params: false, store_google: false, person_profiles: 'identified_only', respect_dnt: true,
    before_send: event => {
      if (!event || !permitted() || !events.has(event.event)) return null
      event.properties = browserProperties(event.properties ?? {}, context(), { token: config!.token!, origin: location.origin, hostname: location.hostname, pathname: location.pathname })
      // Never let SDK-generated identity properties carry a raw URL/title/referrer.
      if (event.$set) event.$set = cleanProperties(event.$set)
      if (event.$set_once) event.$set_once = cleanProperties(event.$set_once)
      if (config!.environment === 'test') console.info('[analytics:test]', JSON.stringify({ event: event.event, properties: { ...event.properties, token: '[public project token]' } }))
      return event
    },
  })
  started = true
  posthog.opt_in_capturing({ captureEventName: false })
  try { first = cleanProperties(JSON.parse(readCookie('smkt_analytics_first_v1') || '{}')) as Record<string, string> } catch { first = {} }
  if (!first.landing_path) { first = entry; writeCookie('smkt_analytics_first_v1', JSON.stringify(first)) }
  pageview()
  for (const observe of [onCLS, onINP, onLCP, onFCP, onTTFB]) observe(metric => capture('web_vital', { page_path: entry.landing_path, metric: metric.name, metric_value: metric.value, rating: metric.rating }))
}
export function capture(event: string, properties: Record<string, unknown> = {}) {
  if (!started || !permitted() || !events.has(event)) return
  try { posthog.capture(event, cleanProperties(properties)) } catch { /* Analytics must never break the app. */ }
}
export function identify(id: string | undefined, internal = false) {
  if (!started || !permitted()) return
  internalUser = internal
  if (!id) { if (userId || posthog.get_property('$user_id')) { posthog.reset(true); userId = undefined; session = {}; lastSession = ''; first = {}; writeCookie('smkt_analytics_first_v1', '', 0); writeCookie('smkt_analytics_session_v1', '', 0) }; return }
  if (!/^[A-Za-z0-9_-]{1,100}$/.test(id) || userId === id) return
  const previousId = userId ?? posthog.get_property('$user_id')
  if (previousId && previousId !== id) posthog.reset(true)
  userId = id
  posthog.identify(id, { is_internal: internal, analytics_environment: config!.environment, is_test_event: config!.environment !== 'production' })
}
export function analyticsHeaders(): Record<string, string> {
  if (!started || !permitted()) return {}
  return { 'x-smkt-analytics': 'yes', 'x-smkt-anonymous-id': String(posthog.get_distinct_id()), 'x-smkt-session-id': posthog.get_session_id(), 'x-smkt-surface': config!.surface }
}
export function pageview() {
  if (!started || !permitted()) return
  if (activePath === location.pathname) { observeSections(); return }
  if (activePath) capture('$pageleave', { page_path: safePath(activePath), engaged_seconds: Math.round(elapsed / 1000) })
  activePath = location.pathname; elapsed = 0; lastTick = Date.now(); milestones = new Set()
  capture('$pageview')
  sections = new Set(); observeSections()
}
function observeSections() {
  sectionObserver?.disconnect()
  if (config!.surface !== 'platform' && !/^\/(app|oauth|signup|login)(\/|$)/.test(location.pathname)) {
    sectionObserver = new IntersectionObserver(entries => {
      for (const item of entries) {
        const key = (item.target as HTMLElement).dataset.analyticsSection
        if (item.isIntersecting && key && /^[a-z0-9-]{1,60}$/.test(key) && !sections.has(key)) { sections.add(key); capture('section_viewed', { section_id: key }) }
      }
    }, { threshold: 0.3 })
    // Router commits can follow the location notification.
    requestAnimationFrame(() => document.querySelectorAll('[data-analytics-section]').forEach(el => sectionObserver?.observe(el)))
  }
}
export function initAnalytics(value: AnalyticsConfig) {
  if (config) return
  config = value; entry = attribution(location.href, document.referrer)
  start()
  // History events are owned by the router integration; no history monkey patching.
  document.addEventListener('click', event => {
    const el = (event.target as Element)?.closest?.('a[href], [data-analytics-event]') as HTMLElement | null
    if (!el || el.closest('[data-analytics-ignore]')) return
    const name = el.dataset.analyticsEvent
    if (name && !(el instanceof HTMLAnchorElement)) { capture(name, { placement: el.dataset.analyticsPlacement, plan: el.dataset.analyticsPlan }); return }
    if (!(el instanceof HTMLAnchorElement)) return
    const url = new URL(el.href, location.href)
    const publicPage = config!.surface === 'marketing' || (config!.surface === 'pricing' && !/^\/(app|oauth|login|sign-in|signup|accept-invitation)(\/|$)/.test(location.pathname))
    if (!publicPage) return
    const placement = el.closest('header') ? 'header' : el.closest('footer') ? 'footer' : 'body'
    capture(name === 'cta_clicked' ? 'cta_clicked' : 'navigation_clicked', { destination_path: /^https?:$/.test(url.protocol) ? safePath(url.pathname) : '/contact', destination_host: /^https?:$/.test(url.protocol) ? url.hostname : url.protocol.replace(':',''), placement })
  }, true)
  window.addEventListener('scroll', () => {
    if (!permitted()) return
    const height = document.documentElement.scrollHeight - innerHeight
    const percentage = height > 0 ? Math.round(scrollY / height * 100) : 0
    for (const milestone of [25,50,75,100]) if (percentage >= milestone && !milestones.has(milestone)) { milestones.add(milestone); capture('scroll_milestone', { milestone }) }
  }, { passive: true })
  setInterval(() => {
    const now = Date.now()
    if (started && permitted() && document.visibilityState === 'visible' && document.hasFocus()) {
      elapsed += Math.min(now - lastTick, 5000)
      if (elapsed >= 30000 && !milestones.has(30)) { milestones.add(30); capture('page_engaged', { engaged_seconds: 30 }) }
    }
    lastTick = now
  }, 1000)
  window.addEventListener('pagehide', () => capture('$pageleave', { engaged_seconds: Math.round(elapsed / 1000) }))
}
